import { CoreEvent, eventNames, useCoreEvents } from '@carvana/core-events';
import { useCallback, useContext, useEffect } from 'react';
import { useUserLocation } from '~/context/common/UserContext';
import { useIsComposerReady } from '~/context/composer/ComposerContext';
import ModalContext from '~/context/vdp/ModalContext';
import UserVehicleContext from '~/context/vdp/UserVehicleContext';
import { useGetVehicleDetails } from '~/context/vdp/VehicleContext';
import useAlertStatus from '~/hooks/vdp/selectors/useAlertStatus';
import useIsInExclusivePreview from '~/hooks/vdp/selectors/useIsInExclusivePreview';
import usePrimaryCtaButtonState from '~/hooks/vdp/selectors/usePrimaryCtaButtonState';
import deriveVehicleDescription from '~/utilities/vdp/deriveVehicleDescription';

export default function useComposerEvents() {
  const { setComposerUiModalOpen } = useContext(ModalContext);
  const isComposerReady = useIsComposerReady();
  const { zip } = useUserLocation();
  const {
    bodyType: bodyStyles,
    fuelDescription: fuelTypes,
    make,
    model,
    trim,
    vehicleId,
    year
  } = useGetVehicleDetails();
  const { vehicleAvailability } = useContext(UserVehicleContext);

  const {
    isReservedByOtherAndSubscribed,
    isReservedByUserAndKicked,
    type: ctaType
  } = usePrimaryCtaButtonState();
  const {
    isSold,
    notAvailableInCA,
    outOfMarket,
    outOfMarketNewCar,
    purchasePending,
    stopSaleRecall
  } = useAlertStatus();
  const isInExclusivePreview = useIsInExclusivePreview();

  const isOutOfArea = notAvailableInCA || outOfMarket || outOfMarketNewCar;
  const { description: vehicleStateDescription, scenario: vehicleState } =
    vehicleAvailability.isLoaded
      ? deriveVehicleDescription({
          ctaType,
          isInExclusivePreview,
          isOutOfArea,
          isReservedByOtherAndSubscribed,
          isReservedByUserAndKicked,
          isSold,
          purchasePending,
          stopSaleRecall
        })
      : { description: undefined, scenario: undefined };

  useCoreEvents(
    eventNames.composer.mfe.opened,
    useCallback(
      ({ source }) => {
        if (source !== 'composer') return;

        setComposerUiModalOpen(true);
      },
      [setComposerUiModalOpen]
    )
  );
  useCoreEvents(
    eventNames.composer.mfe.closed,
    useCallback(
      ({ source }) => {
        if (source !== 'composer') return;

        setComposerUiModalOpen(false);
      },
      [setComposerUiModalOpen]
    )
  );

  useEffect(() => {
    if (!isComposerReady || !vehicleId) return;

    CoreEvent.emit(eventNames.composer.pageContext, {
      context: {
        capabilities: [],
        ...(bodyStyles && { bodyStyles }),
        ...(fuelTypes && { fuelTypes }),
        ...(make && { make }),
        ...(model && { model }),
        ...(trim && { trim }),
        url: window.location.href,
        vehicleId,
        ...(vehicleState && { vehicleState }),
        ...(vehicleStateDescription && { vehicleStateDescription }),
        ...(year && { year }),
        zip5: zip
      },
      source: 'page',
      surface: 'vdp'
    });
  }, [
    bodyStyles,
    fuelTypes,
    isComposerReady,
    make,
    model,
    trim,
    vehicleId,
    vehicleState,
    vehicleStateDescription,
    year,
    zip
  ]);
}
